// Desktop drag-and-drop of files from the OS into the open conversation. Listens on the
// document (not on the composer/history nodes) because renderShell() rebuilds those while a
// drag is in progress. Only drags carrying files react; text/link drags inside the page keep
// their native behaviour.
export type FileDropOptions = {
  // Whether the element under the pointer is a drop zone (history, composer, attach button).
  accepts: (target: EventTarget | null) => boolean;
  drop: (files: File[]) => void;
  highlight: (active: boolean) => void;
};

export function carriesFiles(transfer: DataTransfer | null | undefined): boolean {
  return Boolean(transfer && [...transfer.types].includes('Files'));
}

export function installFileDrop(target: EventTarget, options: FileDropOptions): void {
  let active = false;
  let idle: ReturnType<typeof setTimeout> | null = null;
  const set = (next: boolean): void => {
    if (idle !== null) { clearTimeout(idle); idle = null; }
    if (active !== next) { active = next; options.highlight(next); }
  };
  target.addEventListener('dragover', (raw) => {
    const event = raw as DragEvent;
    if (!carriesFiles(event.dataTransfer) || !options.accepts(event.target)) { set(false); return; }
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    set(true);
    // dragover repeats while the pointer stays over the page; when it stops (pointer left the
    // window, Esc) there is no reliable dragleave, so the highlight clears itself.
    idle = setTimeout(() => set(false), 200);
  });
  target.addEventListener('drop', (raw) => {
    const event = raw as DragEvent;
    set(false);
    if (!carriesFiles(event.dataTransfer) || !options.accepts(event.target)) return;
    event.preventDefault();
    const files = [...event.dataTransfer?.files ?? []];
    if (files.length) options.drop(files);
  });
}
